/**
 * Pure layout for the HQ bay facade and its exterior height proxies.
 *
 * Keeping these numbers free of PlayCanvas makes the facade contract cheap to
 * test and gives P7 stable, deterministic slots for health bands and boats.
 * Coordinates are HQ-local: +X is east/the bay and +Z is south.
 */

export const HQ_FACADE_X = 24
export const HQ_FACADE_MIN_Z = -15
export const HQ_FACADE_MAX_Z = 15
export const HQ_FACADE_MODULE = 6
export const HQ_STOREY_HEIGHT = 4.5
export const HQ_VISION_HEAD_OFFSET = 3.9
export const HQ_SPANDREL_HEIGHT = 0.6

export interface FacadeLevel {
  floor: 0 | 1 | 2 | 3
  walkY: number
  parapetHeight: number
}

/** The progressively lighter edge condition is the principal altitude cue. */
export const HQ_FACADE_LEVELS: readonly FacadeLevel[] = [
  { floor: 0, walkY: 0, parapetHeight: 1.35 },
  { floor: 1, walkY: 4.5, parapetHeight: 1.10 },
  { floor: 2, walkY: 9, parapetHeight: 0.95 },
  { floor: 3, walkY: 13.5, parapetHeight: 0.60 },
]

export interface FacadePanel {
  floor: FacadeLevel['floor']
  module: number
  minZ: number
  maxZ: number
  minY: number
  maxY: number
  parapetHeight: number
}

/** Five independently cullable six-metre panes on each occupied floor. */
export const HQ_CURTAIN_PANELS: readonly FacadePanel[] = HQ_FACADE_LEVELS.flatMap((level) =>
  Array.from({ length: (HQ_FACADE_MAX_Z - HQ_FACADE_MIN_Z) / HQ_FACADE_MODULE }, (_, module) => ({
    floor: level.floor,
    module,
    minZ: HQ_FACADE_MIN_Z + module * HQ_FACADE_MODULE,
    maxZ: HQ_FACADE_MIN_Z + (module + 1) * HQ_FACADE_MODULE,
    minY: level.walkY + level.parapetHeight,
    maxY: level.walkY + HQ_VISION_HEAD_OFFSET,
    parapetHeight: level.parapetHeight,
  })),
)

export interface FacadeMullion {
  floor: FacadeLevel['floor']
  z: number
  minY: number
  maxY: number
}

/** Three-metre bronze bay rhythm, split per storey for culling. */
export const HQ_CURTAIN_MULLIONS: readonly FacadeMullion[] = HQ_FACADE_LEVELS.flatMap((level) =>
  Array.from({ length: (HQ_FACADE_MAX_Z - HQ_FACADE_MIN_Z) / 3 + 1 }, (_, index) => ({
    floor: level.floor,
    z: HQ_FACADE_MIN_Z + index * 3,
    minY: level.walkY,
    maxY: level.walkY + HQ_STOREY_HEIGHT,
  })),
)

export interface SkylineBlock {
  row: 'near' | 'far'
  x: number
  z: number
  width: number
  height: number
  depth: number
}

const NEAR_SKYLINE: readonly SkylineBlock[] = [
  { row: 'near', x: 120, z: -52, width: 13, height: 31, depth: 12 },
  { row: 'near', x: 120, z: -35, width: 16, height: 43, depth: 13 },
  { row: 'near', x: 120, z: -15, width: 18, height: 27, depth: 14 },
  { row: 'near', x: 120, z: 7, width: 15, height: 49, depth: 12 },
  { row: 'near', x: 120, z: 27, width: 18, height: 35, depth: 14 },
  { row: 'near', x: 120, z: 48, width: 13, height: 40, depth: 11 },
]

const FAR_SKYLINE: readonly SkylineBlock[] = [
  { row: 'far', x: 200, z: -65, width: 18, height: 45, depth: 16 },
  { row: 'far', x: 200, z: -42, width: 20, height: 59, depth: 17 },
  { row: 'far', x: 200, z: -16, width: 24, height: 38, depth: 19 },
  { row: 'far', x: 200, z: 12, width: 22, height: 52, depth: 18 },
  { row: 'far', x: 200, z: 39, width: 21, height: 42, depth: 17 },
  { row: 'far', x: 200, z: 64, width: 17, height: 55, depth: 15 },
]

export const HQ_SKYLINE: readonly SkylineBlock[] = [...NEAR_SKYLINE, ...FAR_SKYLINE]

export interface CloudBillboard {
  x: number
  y: number
  z: number
  width: number
  height: number
}

/** Deterministic cloud deck: high from L0, below eye level from L3. */
export const HQ_CLOUDS: readonly CloudBillboard[] = Array.from({ length: 18 }, (_, index) => ({
  x: 42 + (index % 6) * 21 + Math.floor(index / 6) * 4,
  y: 4.2 + (index % 4) * 0.85,
  z: -72 + (index * 29) % 145,
  width: 8 + (index % 3) * 3,
  height: 1.5 + (index % 2) * 0.7,
}))

export const HQ_WATER_Y = -8
export const HQ_WATER_MIN_X = HQ_FACADE_X
export const HQ_WATER_MAX_X = 300
export const HQ_COMPLETION_BOAT_CAP = 8

export interface FacadeBatchGroup {
  key: string
  maxAabbSize: number
}

/**
 * Static merge zones. The four storeys stay independently cullable; near
 * shore and each skyline depth also disappear as a unit. Water/sun-streak are
 * already one draw each, and disabled boats must remain outside batching.
 */
export const HQ_FACADE_BATCH_GROUPS: readonly FacadeBatchGroup[] = [
  ...HQ_FACADE_LEVELS.map((level) => ({ key: `curtain-l${level.floor}`, maxAabbSize: 32 })),
  { key: 'shore', maxAabbSize: 25 },
  { key: 'skyline-near', maxAabbSize: 160 },
  { key: 'skyline-far', maxAabbSize: 160 },
  { key: 'cloud-deck', maxAabbSize: 160 },
]

/**
 * Maximum render entities owned by the facade, including the disabled boat
 * pool. This is an allocation bound, NOT the draw-call budget.
 */
export const HQ_FACADE_RENDER_CAP =
  HQ_CURTAIN_PANELS.length * 3 + // glass + parapet + health spandrel
  HQ_CURTAIN_MULLIONS.length +
  HQ_CURTAIN_PANELS.length + // one mid-floor transom per six-metre module
  2 + // water + sun streak
  10 + // five boardwalk and five seawall modules
  HQ_SKYLINE.length * 3 + // mass + crown + one bounded window-light strip
  HQ_CLOUDS.length +
  HQ_COMPLETION_BOAT_CAP * 2 // hull + sail, disabled until P7

/**
 * Expected facade draws after static batching: four materials × four curtain
 * floors, two shore materials, two materials × two skyline rows, one cloud
 * material, plus the two already-single water surfaces. Boats are disabled.
 */
export const HQ_FACADE_EXPECTED_STATIC_DRAWS = 16 + 2 + 4 + 1 + 2
